import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, AlertTriangle, Clock, DollarSign, FileText,
  RefreshCw, ChevronDown, ChevronUp, Send, User, Calendar, Search,
  Filter, Eye, X, Shield, Zap
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (amount, currency = 'XOF') => {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount);
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })?.format(new Date(d));
};

const SEVERITY_CONFIG = {
  low:      { label: 'Faible',    bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  dot: 'bg-green-500' },
  medium:   { label: 'Moyen',     bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  dot: 'bg-amber-500' },
  high:     { label: 'Élevé',     bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  critical: { label: 'Critique',  bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    dot: 'bg-red-500' },
};

const STATUS_CONFIG = {
  pending:      { label: 'En attente',    bg: 'bg-gray-100',   text: 'text-gray-600',   icon: Clock },
  routed:       { label: 'Routé',         bg: 'bg-blue-100',   text: 'text-blue-700',   icon: Send },
  under_review: { label: 'En révision',   bg: 'bg-purple-100', text: 'text-purple-700', icon: Eye },
  resolved:     { label: 'Résolu',        bg: 'bg-green-100',  text: 'text-green-700',  icon: CheckCircle },
  rejected:     { label: 'Rejeté',        bg: 'bg-red-100',    text: 'text-red-700',    icon: XCircle },
};

const SeverityBadge = ({ level }) => {
  const cfg = SEVERITY_CONFIG?.[level] || SEVERITY_CONFIG?.medium;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${cfg?.bg} ${cfg?.text} ${cfg?.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg?.dot}`} />
      {cfg?.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.pending;
  const IconComp = cfg?.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg?.bg} ${cfg?.text}`}>
      <IconComp className="w-3 h-3" />
      {cfg?.label}
    </span>
  );
};

// ─── Resolution Modal ─────────────────────────────────────────────────────────
const ResolutionModal = ({ dispute, onClose, onResolved }) => {
  const [form, setForm] = useState({
    resolution_summary: '',
    resolution_type: 'full_release',
    fund_adjustment_amount: '',
    fund_adjustment_direction: 'refund_buyer',
    notify_parties: true,
    internal_note: '',
    final_status: 'resolved',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const resolutionTypes = [
    { value: 'full_release', label: 'Libération complète des fonds au vendeur' },
    { value: 'full_refund', label: 'Remboursement complet à l\'acheteur' },
    { value: 'partial_adjustment', label: 'Ajustement partiel des fonds' },
    { value: 'no_adjustment', label: 'Résolution sans ajustement financier' },
  ];

  const handleSubmit = async () => {
    if (!form?.resolution_summary?.trim()) {
      setError('Le résumé de résolution est obligatoire.');
      return;
    }
    if (form?.resolution_type === 'partial_adjustment' && !form?.fund_adjustment_amount) {
      setError('Veuillez saisir le montant de l\'ajustement.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updatePayload = {
        status: form?.final_status,
        resolution_summary: form?.resolution_summary,
        resolved_at: new Date()?.toISOString(),
      };

      const { error: updateErr } = await supabase?.from('escrow_dispute_escalations')?.update(updatePayload)?.eq('id', dispute?.id);

      if (updateErr) throw updateErr;

      // Log to escrow_action_log if table exists
      try {
        await supabase?.from('escrow_action_log')?.insert({
          action_type: 'dispute_resolved',
          reference: dispute?.transaction_ref,
          performed_by_name: 'Admin Finance',
          notes: `Litige clôturé: ${form?.resolution_summary}. Type: ${form?.resolution_type}${form?.fund_adjustment_amount ? `. Ajustement: ${fmt(parseFloat(form?.fund_adjustment_amount))}` : ''}`,
          metadata: {
            dispute_id: dispute?.id,
            resolution_type: form?.resolution_type,
            fund_adjustment_amount: form?.fund_adjustment_amount || null,
            fund_adjustment_direction: form?.fund_adjustment_direction,
            notify_parties: form?.notify_parties,
          },
        });
      } catch (_) { /* non-blocking */ }

      // Send notification emails if requested
      if (form?.notify_parties) {
        try {
          const ESCROW_EMAIL_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-escrow-emails`;
          const { data: sessionData } = await supabase?.auth?.getSession();
          const token = sessionData?.session?.access_token;
          await fetch(ESCROW_EMAIL_URL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              type: 'dispute_resolved',
              data: {
                transaction_ref: dispute?.transaction_ref,
                title: dispute?.title,
                resolution_summary: form?.resolution_summary,
                resolution_type: form?.resolution_type,
                fund_adjustment_amount: form?.fund_adjustment_amount || null,
                severity_level: dispute?.severity_level,
                submitted_by_name: dispute?.submitted_by_name,
                submitted_by_email: dispute?.submitted_by_email,
              },
            }),
          });
        } catch (_) { /* non-blocking */ }
      }

      onResolved();
    } catch (err) {
      setError(err?.message || 'Erreur lors de la clôture du litige.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Clôturer le litige</h3>
              <p className="text-sm text-gray-500">Réf: {dispute?.transaction_ref || dispute?.id?.slice(0, 8)}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Dispute summary */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">{dispute?.title}</span>
              <SeverityBadge level={dispute?.severity_level} />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Montant en litige</span>
                <p className="font-semibold text-gray-900">{fmt(dispute?.amount, dispute?.currency)}</p>
              </div>
              <div>
                <span className="text-gray-500">Soumis par</span>
                <p className="font-semibold text-gray-900">{dispute?.submitted_by_name || '—'}</p>
              </div>
              <div>
                <span className="text-gray-500">Catégorie</span>
                <p className="font-medium text-gray-700">{dispute?.category || '—'}</p>
              </div>
              <div>
                <span className="text-gray-500">Date de soumission</span>
                <p className="font-medium text-gray-700">{fmtDate(dispute?.created_at)}</p>
              </div>
            </div>
            {dispute?.description && (
              <p className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">{dispute?.description}</p>
            )}
          </div>

          {/* Resolution type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Type de résolution <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 gap-2">
              {resolutionTypes?.map(rt => (
                <label key={rt?.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  form?.resolution_type === rt?.value
                    ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="resolution_type"
                    value={rt?.value}
                    checked={form?.resolution_type === rt?.value}
                    onChange={e => setForm(f => ({ ...f, resolution_type: e?.target?.value }))}
                    className="text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">{rt?.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Fund adjustment */}
          {form?.resolution_type === 'partial_adjustment' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Montant ajustement (XOF) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={form?.fund_adjustment_amount}
                  onChange={e => setForm(f => ({ ...f, fund_adjustment_amount: e?.target?.value }))}
                  placeholder="Ex: 500000"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Direction</label>
                <select
                  value={form?.fund_adjustment_direction}
                  onChange={e => setForm(f => ({ ...f, fund_adjustment_direction: e?.target?.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="refund_buyer">Remboursement acheteur</option>
                  <option value="release_seller">Libération vendeur</option>
                  <option value="platform_fee">Frais plateforme</option>
                </select>
              </div>
            </div>
          )}

          {/* Resolution summary */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Résumé de résolution <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form?.resolution_summary}
              onChange={e => setForm(f => ({ ...f, resolution_summary: e?.target?.value }))}
              rows={4}
              placeholder="Décrivez la décision prise, les raisons et les actions effectuées..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Final status */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Statut final</label>
            <div className="flex gap-3">
              {[
                { value: 'resolved', label: 'Résolu', color: 'border-green-500 bg-green-50 text-green-700' },
                { value: 'rejected', label: 'Rejeté', color: 'border-red-500 bg-red-50 text-red-700' },
              ]?.map(s => (
                <label key={s?.value} className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                  form?.final_status === s?.value ? s?.color : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="final_status"
                    value={s?.value}
                    checked={form?.final_status === s?.value}
                    onChange={e => setForm(f => ({ ...f, final_status: e?.target?.value }))}
                    className="sr-only"
                  />
                  <span className="text-sm font-semibold">{s?.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Notify parties */}
          <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={form?.notify_parties}
              onChange={e => setForm(f => ({ ...f, notify_parties: e?.target?.checked }))}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <div>
              <p className="text-sm font-semibold text-gray-700">Notifier toutes les parties</p>
              <p className="text-xs text-gray-500">Envoie un email de résolution au soumetteur et aux parties concernées</p>
            </div>
          </label>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Clôture en cours...</>
            ) : (
              <><CheckCircle className="w-4 h-4" /> Clôturer le litige</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Dispute Card ─────────────────────────────────────────────────────────────
const DisputeCard = ({ dispute, onResolve }) => {
  const [expanded, setExpanded] = useState(false);
  const isResolved = ['resolved', 'rejected']?.includes(dispute?.status);

  return (
    <div className={`bg-white rounded-xl border transition-all ${
      isResolved ? 'border-gray-200 opacity-75' : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
    }`}>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <SeverityBadge level={dispute?.severity_level} />
              <StatusBadge status={dispute?.status} />
              {dispute?.transaction_ref && (
                <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {dispute?.transaction_ref}
                </span>
              )}
            </div>
            <h4 className="text-sm font-bold text-gray-900 truncate">{dispute?.title}</h4>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {dispute?.submitted_by_name || 'Inconnu'}
                {dispute?.submitted_by_role && ` · ${dispute?.submitted_by_role}`}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {fmtDate(dispute?.created_at)}
              </span>
              {dispute?.amount && (
                <span className="flex items-center gap-1 font-semibold text-gray-700">
                  <DollarSign className="w-3 h-3" />
                  {fmt(dispute?.amount, dispute?.currency)}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!isResolved && (
              <button
                onClick={() => onResolve(dispute)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
              >
                <Zap className="w-3 h-3" />
                Clôturer
              </button>
            )}
            <button
              onClick={() => setExpanded(e => !e)}
              className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
            {dispute?.description && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</p>
                <p className="text-sm text-gray-700 leading-relaxed">{dispute?.description}</p>
              </div>
            )}
            {dispute?.category && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Catégorie:</span>
                <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{dispute?.category}</span>
              </div>
            )}
            {dispute?.timeline_notes && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Notes de timeline</p>
                <p className="text-sm text-gray-600">{dispute?.timeline_notes}</p>
              </div>
            )}
            {dispute?.resolution_summary && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-1">Résolution</p>
                <p className="text-sm text-green-800">{dispute?.resolution_summary}</p>
                {dispute?.resolved_at && (
                  <p className="text-xs text-green-600 mt-1">Clôturé le {fmtDate(dispute?.resolved_at)}</p>
                )}
              </div>
            )}
            {Array.isArray(dispute?.evidence_files) && dispute?.evidence_files?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                  Preuves ({dispute?.evidence_files?.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {dispute?.evidence_files?.map((f, i) => (
                    <a
                      key={i}
                      href={typeof f === 'string' ? f : f?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 bg-blue-50 px-2 py-1 rounded"
                    >
                      <FileText className="w-3 h-3" />
                      {typeof f === 'string' ? `Fichier ${i + 1}` : (f?.name || `Fichier ${i + 1}`)}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const DisputeResolutionPanel = () => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [resolvingDispute, setResolvingDispute] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchDisputes = useCallback(async () => {
    try {
      setRefreshing(true);
      const { data, error } = await supabase?.from('escrow_dispute_escalations')?.select('*')?.order('created_at', { ascending: false });
      if (error) throw error;
      setDisputes(data || []);
    } catch (err) {
      console.error('[DisputeResolutionPanel] fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchDisputes(); }, [fetchDisputes]);

  const handleResolved = () => {
    setResolvingDispute(null);
    showToast('Litige clôturé avec succès. Les parties ont été notifiées.');
    fetchDisputes();
  };

  const filtered = disputes?.filter(d => {
    const matchSearch = !search || [d?.title, d?.transaction_ref, d?.submitted_by_name, d?.category]?.some(v => v?.toLowerCase()?.includes(search?.toLowerCase()));
    const matchStatus = filterStatus === 'all' || d?.status === filterStatus;
    const matchSeverity = filterSeverity === 'all' || d?.severity_level === filterSeverity;
    return matchSearch && matchStatus && matchSeverity;
  });

  const stats = {
    total: disputes?.length,
    pending: disputes?.filter(d => ['pending', 'routed', 'under_review']?.includes(d?.status))?.length,
    resolved: disputes?.filter(d => d?.status === 'resolved')?.length,
    critical: disputes?.filter(d => d?.severity_level === 'critical' && d?.status !== 'resolved')?.length,
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast?.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast?.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {toast?.msg}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total litiges', value: stats?.total, icon: FileText, color: 'bg-blue-100 text-blue-600' },
          { label: 'En attente', value: stats?.pending, icon: Clock, color: 'bg-amber-100 text-amber-600' },
          { label: 'Résolus', value: stats?.resolved, icon: CheckCircle, color: 'bg-green-100 text-green-600' },
          { label: 'Critiques actifs', value: stats?.critical, icon: AlertTriangle, color: 'bg-red-100 text-red-600' },
        ]?.map(s => (
          <div key={s?.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s?.color}`}>
                <s.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{s?.value}</p>
                <p className="text-xs text-gray-500">{s?.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-48 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              placeholder="Rechercher par titre, référence, soumetteur..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e?.target?.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="routed">Routé</option>
              <option value="under_review">En révision</option>
              <option value="resolved">Résolu</option>
              <option value="rejected">Rejeté</option>
            </select>
            <select
              value={filterSeverity}
              onChange={e => setFilterSeverity(e?.target?.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Toutes sévérités</option>
              <option value="low">Faible</option>
              <option value="medium">Moyen</option>
              <option value="high">Élevé</option>
              <option value="critical">Critique</option>
            </select>
          </div>
          <button
            onClick={fetchDisputes}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(4)]?.map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-5 w-16 bg-gray-200 rounded-full" />
                <div className="h-5 w-20 bg-gray-200 rounded-full" />
              </div>
              <div className="h-4 w-2/3 bg-gray-200 rounded" />
              <div className="h-3 w-1/2 bg-gray-100 rounded mt-2" />
            </div>
          ))}
        </div>
      ) : filtered?.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Aucun litige trouvé</p>
          <p className="text-sm text-gray-400 mt-1">Modifiez vos filtres ou revenez plus tard.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered?.map(d => (
            <DisputeCard
              key={d?.id}
              dispute={d}
              onResolve={setResolvingDispute}
            />
          ))}
        </div>
      )}

      {/* Resolution Modal */}
      {resolvingDispute && (
        <ResolutionModal
          dispute={resolvingDispute}
          onClose={() => setResolvingDispute(null)}
          onResolved={handleResolved}
        />
      )}
    </div>
  );
};

export default DisputeResolutionPanel;
